import { FC, useEffect, useState } from 'react'
import { Box, SelectChangeEvent, Table, TableBody, TableCell, TableHead, Typography } from '@mui/material'
import axios from '../../../../lib/axios'
import dayjs, { Dayjs } from 'dayjs'
import { DateRange } from '@mui/x-date-pickers-pro'
import theme from 'theme'

/* Utils */
import { AuditLogRecord, AuditLogRecordPayload, User } from '../../../../models'
import { UserServiceV4 } from 'services'
import { defaultLimitOptions, getPayloadActions, getPayloadDateRange, getPayloadEmails } from './utils'
import { breadcrumbs, dateFormat } from '../../../../lib/utils'

/* Components */
import {
  AuditLogContainer,
  ClearFilters,
  Container,
  EmptyRow,
  LogAction,
  TableActionsWrapper,
  TableBodyRow,
  TableContainer,
  TableHeader,
  TableHeadRow
} from './auditLog.styles'
import {
  AuditAcceptedInvitationIcon,
  AuditAccessPermissionChangeIcon,
  AuditActivatedAccountIcon,
  AuditAddNewUserIcon,
  AuditAssignedRolesIcon,
  AuditCPIActivatedIcon,
  AuditCPIDeactivatedIcon,
  AuditDeleteUserIcon,
  AuditEditUserDetailsIcon,
  AuditFailedLoginIcon,
  AuditInstanceAddedIcon,
  AuditInstanceConfigurationChangedIcon,
  AuditInstanceDeletedIcon,
  AuditLoginIcon,
  AuditLogoutIcon,
  AuditRemovedRolesIcon,
  AuditResetPasswordIcon,
  AuditSLAChangedIcon,
  PaginationEndIcon,
  PaginationNextIcon
} from '../../../components/svg'
// import AuditLogSelectField from './components/AuditLogSelectField'
import ThemeButton from '../../../components/buttons/primary/ThemeButton'
import AuditLogSingleSelectField from './components/AuditLogSingleSelectField'
import AuditDatePicker from './components/AuditDatePicker'
import HeaderComponent from '../../../components/header/HeaderComponent'
import ToolbarComponent from '../../../components/header/ToolbarComponent'
import ClearIcon from '../../../components/svg/ClearIcon'
import ContentWrapper from '../../../components/header/ContentWrapper'
import JiraDropdownMultiple from '../integrations/jira-integration/jira-dropdowns/JiraDropdownMultiple'
import AuditLogSelect from './components/AuditLogSelect'

export interface ActionOption {
  id: string
  name: string
}

const AuditLog: FC = () => {
  const [action, setAction] = useState<string[]>([''])
  const [actionOptions, setActionOptions] = useState<ActionOption[]>([])
  const [user, setUser] = useState<User[]>([])
  const [userOptions, setUserOptions] = useState<User[]>([])
  const [records, setRecords] = useState<AuditLogRecord[]>([])
  // const [allRecords, setAllRecords] = useState<AuditLogRecord[]>([])
  const [limit, setLimit] = useState<number>(Number(defaultLimitOptions[0].name))
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [initialLoad, setInitialLoad] = useState(false)
  // const defaultStart = dayjs(new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000))
  const defStart = dayjs(new Date()).subtract(3, 'months')
  const defaultStart = dayjs(defStart)
  const defaultEnd = dayjs(new Date())
  const [dateValues, setDateValues] = useState<DateRange<Dayjs>>([defaultStart, defaultEnd])

  const fetchActions = async () => {
    const res = await axios.get('/api/v1/audits/actions')

    if (res.status === 200) {
      const { data } = res.data
      setActionOptions(data.sort((a: any, b: any) => (a.name > b.name ? 1 : -1)))
    }
  }

  const fetchUsers = async () => {
    try {
      const { users } = await UserServiceV4.listUsers()
      const sortedUsers = [...users].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      setUserOptions(sortedUsers)

      return sortedUsers
    } catch (err) {
      console.error(err)
    }

    return []
  }

  const fetchLogs = async (userOptions?: User[]) => {
    try {
      const daterange = getPayloadDateRange(dateValues)
      const payload: AuditLogRecordPayload = {
        daterange
      }
      if (userOptions) {
        const filteredUsers = userOptions.filter((c) => !c.email.includes('@onyxia.io'))
        const emails = getPayloadEmails(filteredUsers, userOptions)
        setUser(filteredUsers.length > 0 ? filteredUsers : [])
        if (emails) {
          payload.emails = emails
        }
      }

      const res = await axios.post('/api/v1/audits/records?offset=0', payload ?? {})

      if (res.status === 200) {
        const { data, total, offset } = res.data
        setRecords(data.slice(0, 20))
        setTotal(total)
        setLimit(20)
        setOffset(offset)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setInitialLoad(true)
    }
  }

  const updateLogs = async ({
    limit,
    offset,
    payload
  }: {
    limit: number
    offset: number
    payload?: AuditLogRecordPayload
  }) => {
    const res = await axios.post(`/api/v1/audits/records?offset=${offset}&limit=${limit}`, payload ?? {})

    if (res.status === 200) {
      const { data, total, limit, offset } = res.data
      setRecords(data)
      setTotal(total)
      setLimit(limit)
      setOffset(offset)
    }
  }

  const fetchInitialData = async () => {
    try {
      const userOptions = await fetchUsers()
      await Promise.all([fetchActions(), fetchLogs(userOptions)])
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    fetchInitialData().catch((e) => e)
  }, [])

  const handleUserFilter = async (event: SelectChangeEvent<typeof user>) => {
    const {
      target: { value }
    } = event
    setUser(value as User[])

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, value as User[])
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }

    setPage(1)
    setOffset(0)
    await updateLogs({ limit: 20, offset: 0, payload })
  }

  const handleActionFilter = async (event: SelectChangeEvent<typeof action>) => {
    const {
      target: { value }
    } = event
    const selectedActions: string[] = typeof value === 'string' ? value.split(',') : value
    setAction(selectedActions)

    const actions = getPayloadActions(actionOptions, selectedActions)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }

    setPage(1)
    setOffset(0)
    await updateLogs({ limit: 20, offset: 0, payload })
  }

  const handleLimitChange = async (event: SelectChangeEvent<typeof limit>) => {
    const {
      target: { value }
    } = event
    setLimit(Number(value))

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    setOffset(0)
    setPage(1)
    const offset = 0
    await updateLogs({ limit: Number(value), offset, payload })
  }

  const handleClearFilters = async () => {
    setUser([])
    setAction([''])
    setPage(1)
    setOffset(0)
    setDateValues([defaultStart, defaultEnd])
    setLimit(Number(defaultLimitOptions[0].name))

    const daterange = getPayloadDateRange([defaultStart, defaultEnd])
    const payload: AuditLogRecordPayload = {
      daterange
    }

    await updateLogs({ limit, offset: 0, payload })
  }

  const resetUserFilter = async () => {
    setUser([])
    const actions = getPayloadActions(actionOptions, action)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      daterange
    }
    await updateLogs({ limit: 20, offset: 0, payload })
    setPage(1)
  }

  const resetActionFilter = async () => {
    setAction([''])
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      emails,
      daterange
    }
    await updateLogs({ limit: 20, offset: 0, payload })
    setPage(1)
  }

  const getAction = (action: string) => {
    if (!actionOptions.length) return ''
    const res = actionOptions.findIndex((n) => n.id === action)
    if (res) {
      return actionOptions[res]?.name
    }

    return ''
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'user_logged_in':
        return <AuditLoginIcon />
      case 'user_logged_out':
        return <AuditLogoutIcon />
      case 'access_permission_change':
        return <AuditAccessPermissionChangeIcon />
      case 'added_user':
        return <AuditAddNewUserIcon />
      case 'removed_user':
        return <AuditDeleteUserIcon />
      case 'updated_profile':
      case 'changed_password':
        return <AuditEditUserDetailsIcon />
      case 'user_failed_to_login':
        return <AuditFailedLoginIcon />
      case 'activated_account':
        return <AuditActivatedAccountIcon />
      case 'reset_password':
        return <AuditResetPasswordIcon />
      case 'assigned_roles':
        return <AuditAssignedRolesIcon />
      case 'removed_roles':
        return <AuditRemovedRolesIcon />
      case 'accepted_invitation':
        return <AuditAcceptedInvitationIcon />
      case 'instance_added':
      case 'new_instance_added':
        return <AuditInstanceAddedIcon />
      case 'instance_deleted':
        return <AuditInstanceDeletedIcon />
      case 'cpi_activated':
      case 'io_onyxia_events_cpi_activated':
        return <AuditCPIActivatedIcon />
      case 'cpi_deactivated':
      case 'io_onyxia_events_cpi_deactivated':
        return <AuditCPIDeactivatedIcon />
      case 'sla_changed':
      case 'io_onyxia_events_tenant_framework_switched':
        return <AuditSLAChangedIcon />
      case 'instance_config_changed':
        return <AuditInstanceConfigurationChangedIcon />
      default:
        return <></>
    }
  }

  const handleStart = async () => {
    setPage(1)
    setOffset(0)

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    await updateLogs({ limit, offset: 0, payload })
  }

  const handleNext = async () => {
    const newValue = page + 1
    const newOffset = page * offset
    setPage(newValue)
    setOffset(newOffset)

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    await updateLogs({ limit, offset: page * limit, payload })
  }

  const handlePrev = async () => {
    const newValue = page === 1 ? 1 : page - 1
    const newOffset = offset - limit
    setPage(newValue)
    setOffset(newOffset)

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    await updateLogs({ limit, offset: newOffset, payload })
  }

  const handleEnd = async () => {
    const maxPages = Math.floor(total / limit)
    const remainderValue = total % limit
    const offset = total - (remainderValue === 0 ? limit : remainderValue)
    const newPageValue = remainderValue > 0 ? maxPages + 1 : maxPages
    setPage(newPageValue)
    setOffset(offset)

    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(dateValues)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    await updateLogs({ limit, offset, payload })
  }

  const getPaginationText = () => {
    const maxPages = Math.floor(total / limit)

    return `${page === 1 ? page : (page - 1) * limit + 1} - ${page === maxPages + 1 ? total : page * limit} of ${total}`
  }

  const getNextDisabled = () => {
    const maxPages = Math.floor(total / limit)
    const remainderValue = total % limit

    if (remainderValue > 0) {
      return page === maxPages + 1
    }
    return page === maxPages
  }

  const handleDateChange = async (date: DateRange<Dayjs>) => {
    const actions = getPayloadActions(actionOptions, action)
    const emails = getPayloadEmails(userOptions, user)
    const daterange = getPayloadDateRange(date)
    const payload: AuditLogRecordPayload = {
      actions,
      emails,
      daterange
    }
    setDateValues(date)
    await updateLogs({ limit, offset, payload })
  }

  const isClearFiltersDisabled = () => {
    const start = dayjs(defaultStart).format(dateFormat.auditLogDefault)
    const startValue = dayjs(dateValues[0]).format(dateFormat.auditLogDefault)
    const end = dayjs(defaultEnd).format(dateFormat.auditLogDefault)
    const endValue = dayjs(dateValues[1]).format(dateFormat.auditLogDefault)

    const firstCondition = user.length === 1 && user[0].name === ''
    const secondCondition = action.length === 1 && action[0] === ''
    const thirdCondition = start === startValue
    const fourthCondition = end === endValue
    const truthies = [firstCondition, secondCondition, thirdCondition, fourthCondition]

    let result = true
    if (truthies.some((n) => n.toString() === 'false')) {
      result = false
    }

    return result
  }

  return (
    <Container>
      <HeaderComponent breadcrumbs={breadcrumbs.auditLog} />

      <ToolbarComponent>
        <TableHeader>
          <Box className="header-actions">
            <AuditLogSelect
              placeholder="User"
              options={userOptions}
              value={user}
              handleChange={handleUserFilter}
              handleReset={resetUserFilter}
              multiple
              wrapperSx={{
                width: '100%',
                maxWidth: 'unset'
              }}
            />
            <JiraDropdownMultiple
              placeholder="Action"
              options={actionOptions.map((n) => ({ label: n.name, name: n.name }))}
              value={action}
              handleChange={handleActionFilter}
              handleReset={resetActionFilter}
              multiple
              wrapperSx={{
                width: '100%',
                maxWidth: 'unset'
              }}
            />
            <AuditDatePicker dateValues={dateValues} handleDateChange={handleDateChange} />
          </Box>
          <Box className="clear-filters-wrapper">
            <ClearFilters onClick={handleClearFilters} disabled={isClearFiltersDisabled()}>
              <ClearIcon />
              Clear
            </ClearFilters>
          </Box>
        </TableHeader>
      </ToolbarComponent>

      <ContentWrapper line={2}>
        <AuditLogContainer>
          <TableContainer showborder={!records.length ? 'true' : 'false'}>
            <Table>
              {initialLoad && records.length > 0 && (
                <TableHead>
                  <TableHeadRow>
                    <TableCell className="head-title">Date & Time</TableCell>
                    <TableCell className="head-title">User</TableCell>
                    <TableCell className="head-title">Role</TableCell>
                    <TableCell className="head-title">Action</TableCell>
                    <TableCell className="head-title">Description</TableCell>
                  </TableHeadRow>
                </TableHead>
              )}
              <TableBody>
                {records.length > 0 &&
                  records.map((n: AuditLogRecord) => (
                    <TableBodyRow key={n.id}>
                      <TableCell className="timestamp">{dayjs(n.created_at).format('DD MMM YY, hh:mm A')}</TableCell>
                      <TableCell className="name-wrapper">
                        <Typography component="span" className="name">
                          {n.username !== '' ? n.username : 'N/A'}
                        </Typography>
                        <Typography component="span" className="email" data-private>
                          {n.email !== '' ? n.email : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell className="role">
                        {n.role === '' ? 'N/A' : n.role === 'cpi_manager' ? 'manager' : n.role}
                      </TableCell>
                      <TableCell className="log-action">
                        <LogAction>
                          {getActionIcon(n.action)}
                          <Typography>{getAction(n.action)}</Typography>
                        </LogAction>
                      </TableCell>
                      <TableCell className="description">{n.description}</TableCell>
                    </TableBodyRow>
                  ))}
              </TableBody>
            </Table>
            {!initialLoad && (
              <EmptyRow>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="478"
                  height="490"
                  viewBox="0 0 478 490"
                  fill="none"
                  className="icon"
                >
                  <path
                    d="M518.784 497.221C539.28 458.934 551 415.424 551 369.502C551 366.93 550.947 364.358 550.894 361.786C550.867 360.938 550.814 360.116 550.788 359.267C550.708 357.544 550.655 355.847 550.549 354.123C550.496 353.142 550.417 352.161 550.337 351.154C550.258 349.616 550.125 348.052 550.019 346.514C549.913 345.48 549.807 344.446 549.701 343.385C549.568 341.9 549.409 340.416 549.25 338.957C549.117 337.897 548.985 336.836 548.852 335.776C548.667 334.317 548.481 332.859 548.269 331.427C548.136 330.367 547.977 329.333 547.792 328.272C547.58 326.814 547.341 325.382 547.102 323.924C546.917 322.916 546.731 321.882 546.545 320.875C546.28 319.39 545.989 317.932 545.697 316.473C545.511 315.492 545.299 314.485 545.087 313.504C544.769 312.019 544.424 310.508 544.079 308.996C543.867 308.095 543.655 307.167 543.443 306.265C543.045 304.648 542.621 303.031 542.223 301.44C542.011 300.644 541.799 299.849 541.587 299.08C541.083 297.171 540.526 295.288 539.97 293.406C539.837 292.929 539.704 292.425 539.572 291.974C538.113 287.175 536.549 282.456 534.852 277.763C534.666 277.259 534.481 276.755 534.269 276.251C533.606 274.448 532.943 272.672 532.227 270.869C531.962 270.18 531.67 269.49 531.405 268.801C530.769 267.21 530.106 265.619 529.443 264.055C529.125 263.286 528.78 262.543 528.462 261.801C527.799 260.29 527.136 258.805 526.473 257.32C526.102 256.551 525.731 255.756 525.386 254.987C524.697 253.529 524.007 252.097 523.291 250.639C522.92 249.87 522.522 249.101 522.151 248.332C521.435 246.874 520.693 245.468 519.95 244.037C519.553 243.294 519.155 242.552 518.757 241.809C517.988 240.351 517.193 238.919 516.371 237.488C516 236.798 515.602 236.109 515.204 235.393C514.356 233.908 513.481 232.423 512.606 230.939C512.234 230.355 511.89 229.719 511.518 229.109C510.537 227.492 509.53 225.901 508.522 224.31C508.23 223.859 507.939 223.382 507.647 222.905C506.374 220.943 505.075 218.981 503.776 217.045C503.723 216.966 503.67 216.913 503.643 216.833C500.833 212.75 497.942 208.72 494.946 204.796C494.654 204.398 494.336 204 494.045 203.602C492.878 202.091 491.685 200.58 490.492 199.068C490.014 198.485 489.537 197.928 489.086 197.345C488.026 196.046 486.965 194.747 485.878 193.474C485.321 192.838 484.764 192.175 484.207 191.538C483.2 190.372 482.166 189.179 481.132 188.012C480.522 187.323 479.885 186.633 479.275 185.97C478.268 184.83 477.26 183.743 476.253 182.683C475.59 181.967 474.927 181.251 474.238 180.562C473.256 179.501 472.249 178.493 471.241 177.459C470.525 176.744 469.809 176.028 469.12 175.312C468.112 174.331 467.105 173.35 466.097 172.369C465.355 171.653 464.612 170.937 463.843 170.221C462.862 169.267 461.855 168.365 460.847 167.437C460.052 166.721 459.283 165.979 458.487 165.289C457.506 164.414 456.499 163.539 455.491 162.665C454.669 161.922 453.847 161.206 452.999 160.49C452.044 159.668 451.089 158.9 450.135 158.104C449.313 157.415 448.491 156.725 447.642 156.036L447.669 156.062C395.725 114.011 328.826 90.1747 258.003 94.5761C259.064 95.5041 304.114 133.234 347.096 143.972C386.816 153.915 398.748 165.82 398.748 165.82C398.748 165.82 436.506 197.61 390.793 231.389C390.793 231.389 377.164 240.775 378.994 259.547C379.1 260.29 379.259 261.006 379.391 261.748L379.365 261.695C379.418 261.987 379.471 262.278 379.551 262.57C379.657 263.074 379.736 263.551 379.842 264.055C380.001 264.744 380.16 265.46 380.32 266.176C380.452 266.653 380.585 267.157 380.691 267.661C380.876 268.377 381.088 269.092 381.274 269.782C381.407 270.286 381.539 270.789 381.698 271.293C381.91 272.009 382.123 272.725 382.335 273.441C382.494 273.945 382.653 274.448 382.812 274.952C383.051 275.668 383.263 276.384 383.528 277.1C383.687 277.604 383.846 278.107 384.032 278.585C384.27 279.327 384.535 280.043 384.801 280.759C384.96 281.262 385.145 281.74 385.331 282.217C385.596 282.959 385.861 283.675 386.126 284.391C386.312 284.868 386.498 285.346 386.657 285.823C386.948 286.592 387.24 287.308 387.532 288.05C387.717 288.501 387.876 288.925 388.062 289.376C388.354 290.145 388.672 290.887 388.99 291.656C389.149 292.054 389.308 292.478 389.467 292.876C389.812 293.698 390.13 294.493 390.475 295.288C390.608 295.633 390.767 295.978 390.899 296.349C391.27 297.277 391.668 298.179 392.039 299.08C392.278 299.637 392.49 300.194 392.729 300.724C393.02 301.44 393.339 302.182 393.604 302.872C393.842 303.428 394.081 303.985 394.293 304.542C394.505 305.046 394.717 305.549 394.93 306.053C395.168 306.663 395.407 307.246 395.645 307.83C395.805 308.254 395.964 308.678 396.149 309.076C396.414 309.792 396.68 310.455 396.945 311.117C397.024 311.409 397.13 311.674 397.236 311.939C397.608 312.894 397.926 313.795 398.244 314.697C409.381 346.54 412.483 378.967 403.494 409.406C403.096 410.784 402.672 412.19 402.195 413.542C400.816 417.678 399.225 421.761 397.396 425.791C393.71 433.878 389.07 441.779 383.395 449.442C382.414 450.768 381.407 452.093 380.373 453.393C378.331 455.938 376.183 458.43 374.009 460.79C365.232 470.229 355.448 478.024 344.948 484.229C338.372 488.1 331.557 491.334 324.531 493.959C320.315 495.55 316.046 496.876 311.724 498.016C301.648 500.694 291.28 502.152 280.913 502.444C279.428 502.497 277.943 502.523 276.458 502.523C257.208 502.497 237.984 498.493 220.298 490.804C218.946 490.194 217.594 489.584 216.268 488.948C213.563 487.675 210.938 486.323 208.34 484.865C207.041 484.149 205.741 483.407 204.469 482.638C201.923 481.1 199.431 479.483 196.991 477.759C195.745 476.937 194.552 476.036 193.332 475.161C186.146 469.778 179.464 463.627 173.445 456.733C172.438 455.593 171.457 454.453 170.502 453.26C168.566 450.874 166.71 448.408 164.934 445.889C164.032 444.616 163.157 443.317 162.309 442.018C159.763 438.067 157.43 433.984 155.256 429.662C154.566 428.231 153.85 426.799 153.187 425.341C151.172 420.913 149.369 416.326 147.805 411.58C146.346 407.072 145.1 402.512 144.146 397.898C142.846 391.8 141.971 385.622 141.547 379.418C141.335 376.316 141.229 373.214 141.229 370.112C141.229 368.574 141.255 367.009 141.335 365.472C141.494 360.832 141.892 356.218 142.502 351.631C143.987 340.919 146.718 330.393 150.668 320.318C151.225 318.886 151.809 317.454 152.419 316.023C155.494 308.943 159.153 302.076 163.422 295.58C165.119 292.982 166.896 290.41 168.805 287.944C170.688 285.452 172.65 283.012 174.745 280.679C176.786 278.319 178.961 276.013 181.214 273.812C185.51 269.57 190.071 265.619 194.896 262.013C203.302 255.703 212.45 250.373 222.075 246.184C223.454 245.601 224.859 245.018 226.238 244.461C229.048 243.374 231.886 242.34 234.749 241.438C237.613 240.537 240.53 239.715 243.446 239.025C248.166 237.859 251.958 236.931 255.007 236.745C256.227 236.692 257.314 236.745 258.268 236.931C259.25 237.116 260.125 237.461 260.867 237.991C263.121 239.582 264.42 242.79 264.977 248.491C265.056 249.419 265.136 250.453 265.189 251.54C265.507 258.142 263.651 265.301 261.265 271.426C264.261 268.933 267.469 266.759 270.81 264.903C281.947 258.646 294.648 255.65 307.428 254.642C311.273 254.351 315.118 254.218 318.936 254.245C328.8 254.351 341.395 259.415 350.834 258.275C351.471 258.195 352.107 258.116 352.69 258.01C352.902 257.983 353.115 257.93 353.327 257.903C353.671 257.824 354.043 257.771 354.361 257.691C354.626 257.612 354.865 257.532 355.13 257.479C355.368 257.4 355.66 257.347 355.899 257.294C356.164 257.188 356.402 257.108 356.668 257.029C356.853 256.949 357.092 256.896 357.251 256.79C357.543 256.71 357.781 256.604 358.02 256.498C358.179 256.419 358.365 256.366 358.524 256.286C358.762 256.154 359.001 256.021 359.213 255.888C359.346 255.809 359.478 255.756 359.611 255.703C359.85 255.544 360.062 255.411 360.274 255.252C360.38 255.173 360.512 255.093 360.618 255.04C360.831 254.881 361.016 254.722 361.202 254.563C361.308 254.483 361.414 254.404 361.493 254.351C361.706 254.138 361.865 253.953 362.05 253.794C362.13 253.714 362.209 253.635 362.289 253.555C362.448 253.37 362.634 253.157 362.793 252.972C362.846 252.892 362.899 252.813 362.978 252.76C363.137 252.521 363.297 252.309 363.429 252.097C363.482 252.017 363.535 251.964 363.562 251.885C363.721 251.646 363.88 251.408 364.012 251.142C364.039 251.089 364.065 251.036 364.119 250.983C364.251 250.718 364.384 250.453 364.49 250.161C364.516 250.108 364.543 250.055 364.569 250.002C364.702 249.711 364.808 249.419 364.94 249.127C364.967 249.074 364.994 249.021 364.994 249.021C365.1 248.677 365.232 248.358 365.338 248.014V247.961C367.353 241.783 367.513 233.563 373.532 224.893C380.054 215.507 389.6 207.924 390.873 198.008C391.43 193.792 390.475 189.179 387.161 183.823C375.732 165.263 339.194 162.85 320.288 154.949C308.25 149.885 296.477 144.105 285.208 137.476C284.572 137.105 283.936 136.76 283.299 136.362C281.947 135.567 280.621 134.719 279.295 133.897C278.288 133.26 277.28 132.65 276.273 132.014C275.954 131.802 275.663 131.59 275.345 131.378C273.329 130.079 271.341 128.753 269.379 127.401C267.125 125.836 264.871 124.219 262.67 122.549C236.791 103.167 211.893 76.8381 202.109 45.3923C201.26 53.5321 200.438 61.3273 200.279 68.6982C200.253 69.9179 200.253 71.1376 200.279 72.3572C200.385 83.228 202.215 93.2239 207.863 102.318C193.73 101.974 180.79 96.7503 169.097 88.5574C163.9 84.9514 158.915 80.7357 154.222 76.1222C151.862 73.789 149.581 71.3762 147.354 68.8839C128.395 47.6195 113.971 20.3894 104.399 0C104.293 2.75748 104.24 5.54147 104.24 8.35197C104.187 22.378 105.353 36.7752 107.899 50.9868C108.403 53.7973 108.96 56.6608 109.569 59.4713C115.138 84.8189 125.32 109.132 141.123 128.885C108.668 147.048 80.5084 171.653 58.0762 200.58C52.9057 207.288 48.0269 214.182 43.4927 221.314C40.4434 226.033 37.5532 230.886 34.8221 235.791C32.0645 240.722 29.4925 245.733 27.0266 250.798C24.5871 255.888 22.3068 261.032 20.1856 266.255C18.0378 271.479 16.0756 276.755 14.2726 282.111C12.443 287.467 10.8256 292.876 9.3407 298.338C8.59826 301.069 7.90886 303.826 7.24597 306.557C3.98456 320.371 1.78377 334.45 0.723152 348.741C0.192841 355.9 -0.0458022 363.032 0.00723227 370.112C0.139808 393.736 3.3747 416.83 9.36722 438.942C9.95055 441.17 10.5869 443.37 11.2498 445.571C43.8374 555.181 143.721 639.628 263.625 644.745C268.45 644.958 273.276 645.037 278.049 644.984C287.886 644.878 297.644 644.215 307.243 643.049C311.3 642.545 315.356 641.961 319.413 641.299C322.012 640.848 324.637 640.397 327.209 639.893C329.516 639.416 331.796 638.939 334.05 638.435C338.107 637.534 342.137 636.5 346.114 635.412C353.274 633.477 360.327 631.223 367.274 628.704C373.77 626.345 380.134 623.773 386.392 620.936C389.52 619.53 392.596 618.046 395.672 616.508C400.047 614.334 404.369 612.053 408.638 609.641C411.343 608.129 414.021 606.591 416.672 604.974C421.71 601.898 426.642 598.664 431.468 595.297C434.093 593.467 436.718 591.584 439.29 589.649C446.555 584.214 453.555 578.407 460.29 572.282C463.631 569.207 466.919 566.078 470.128 562.843C482.961 549.957 494.548 535.825 504.704 520.712C507.223 516.921 509.662 513.076 512.022 509.152C514.356 505.228 516.636 501.277 518.784 497.221Z"
                    fill={theme.colors.borderLow}
                  />
                </svg>
              </EmptyRow>
            )}
            {initialLoad && !records.length && (
              <EmptyRow>
                <Typography className="text">Sorry, we couldn’t find anything.</Typography>
                <ThemeButton themevariant="primary" onClick={handleClearFilters} className="button">
                  Clear filters
                </ThemeButton>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="478"
                  height="490"
                  viewBox="0 0 478 490"
                  fill="none"
                  className="icon"
                >
                  <path
                    d="M518.784 497.221C539.28 458.934 551 415.424 551 369.502C551 366.93 550.947 364.358 550.894 361.786C550.867 360.938 550.814 360.116 550.788 359.267C550.708 357.544 550.655 355.847 550.549 354.123C550.496 353.142 550.417 352.161 550.337 351.154C550.258 349.616 550.125 348.052 550.019 346.514C549.913 345.48 549.807 344.446 549.701 343.385C549.568 341.9 549.409 340.416 549.25 338.957C549.117 337.897 548.985 336.836 548.852 335.776C548.667 334.317 548.481 332.859 548.269 331.427C548.136 330.367 547.977 329.333 547.792 328.272C547.58 326.814 547.341 325.382 547.102 323.924C546.917 322.916 546.731 321.882 546.545 320.875C546.28 319.39 545.989 317.932 545.697 316.473C545.511 315.492 545.299 314.485 545.087 313.504C544.769 312.019 544.424 310.508 544.079 308.996C543.867 308.095 543.655 307.167 543.443 306.265C543.045 304.648 542.621 303.031 542.223 301.44C542.011 300.644 541.799 299.849 541.587 299.08C541.083 297.171 540.526 295.288 539.97 293.406C539.837 292.929 539.704 292.425 539.572 291.974C538.113 287.175 536.549 282.456 534.852 277.763C534.666 277.259 534.481 276.755 534.269 276.251C533.606 274.448 532.943 272.672 532.227 270.869C531.962 270.18 531.67 269.49 531.405 268.801C530.769 267.21 530.106 265.619 529.443 264.055C529.125 263.286 528.78 262.543 528.462 261.801C527.799 260.29 527.136 258.805 526.473 257.32C526.102 256.551 525.731 255.756 525.386 254.987C524.697 253.529 524.007 252.097 523.291 250.639C522.92 249.87 522.522 249.101 522.151 248.332C521.435 246.874 520.693 245.468 519.95 244.037C519.553 243.294 519.155 242.552 518.757 241.809C517.988 240.351 517.193 238.919 516.371 237.488C516 236.798 515.602 236.109 515.204 235.393C514.356 233.908 513.481 232.423 512.606 230.939C512.234 230.355 511.89 229.719 511.518 229.109C510.537 227.492 509.53 225.901 508.522 224.31C508.23 223.859 507.939 223.382 507.647 222.905C506.374 220.943 505.075 218.981 503.776 217.045C503.723 216.966 503.67 216.913 503.643 216.833C500.833 212.75 497.942 208.72 494.946 204.796C494.654 204.398 494.336 204 494.045 203.602C492.878 202.091 491.685 200.58 490.492 199.068C490.014 198.485 489.537 197.928 489.086 197.345C488.026 196.046 486.965 194.747 485.878 193.474C485.321 192.838 484.764 192.175 484.207 191.538C483.2 190.372 482.166 189.179 481.132 188.012C480.522 187.323 479.885 186.633 479.275 185.97C478.268 184.83 477.26 183.743 476.253 182.683C475.59 181.967 474.927 181.251 474.238 180.562C473.256 179.501 472.249 178.493 471.241 177.459C470.525 176.744 469.809 176.028 469.12 175.312C468.112 174.331 467.105 173.35 466.097 172.369C465.355 171.653 464.612 170.937 463.843 170.221C462.862 169.267 461.855 168.365 460.847 167.437C460.052 166.721 459.283 165.979 458.487 165.289C457.506 164.414 456.499 163.539 455.491 162.665C454.669 161.922 453.847 161.206 452.999 160.49C452.044 159.668 451.089 158.9 450.135 158.104C449.313 157.415 448.491 156.725 447.642 156.036L447.669 156.062C395.725 114.011 328.826 90.1747 258.003 94.5761C259.064 95.5041 304.114 133.234 347.096 143.972C386.816 153.915 398.748 165.82 398.748 165.82C398.748 165.82 436.506 197.61 390.793 231.389C390.793 231.389 377.164 240.775 378.994 259.547C379.1 260.29 379.259 261.006 379.391 261.748L379.365 261.695C379.418 261.987 379.471 262.278 379.551 262.57C379.657 263.074 379.736 263.551 379.842 264.055C380.001 264.744 380.16 265.46 380.32 266.176C380.452 266.653 380.585 267.157 380.691 267.661C380.876 268.377 381.088 269.092 381.274 269.782C381.407 270.286 381.539 270.789 381.698 271.293C381.91 272.009 382.123 272.725 382.335 273.441C382.494 273.945 382.653 274.448 382.812 274.952C383.051 275.668 383.263 276.384 383.528 277.1C383.687 277.604 383.846 278.107 384.032 278.585C384.27 279.327 384.535 280.043 384.801 280.759C384.96 281.262 385.145 281.74 385.331 282.217C385.596 282.959 385.861 283.675 386.126 284.391C386.312 284.868 386.498 285.346 386.657 285.823C386.948 286.592 387.24 287.308 387.532 288.05C387.717 288.501 387.876 288.925 388.062 289.376C388.354 290.145 388.672 290.887 388.99 291.656C389.149 292.054 389.308 292.478 389.467 292.876C389.812 293.698 390.13 294.493 390.475 295.288C390.608 295.633 390.767 295.978 390.899 296.349C391.27 297.277 391.668 298.179 392.039 299.08C392.278 299.637 392.49 300.194 392.729 300.724C393.02 301.44 393.339 302.182 393.604 302.872C393.842 303.428 394.081 303.985 394.293 304.542C394.505 305.046 394.717 305.549 394.93 306.053C395.168 306.663 395.407 307.246 395.645 307.83C395.805 308.254 395.964 308.678 396.149 309.076C396.414 309.792 396.68 310.455 396.945 311.117C397.024 311.409 397.13 311.674 397.236 311.939C397.608 312.894 397.926 313.795 398.244 314.697C409.381 346.54 412.483 378.967 403.494 409.406C403.096 410.784 402.672 412.19 402.195 413.542C400.816 417.678 399.225 421.761 397.396 425.791C393.71 433.878 389.07 441.779 383.395 449.442C382.414 450.768 381.407 452.093 380.373 453.393C378.331 455.938 376.183 458.43 374.009 460.79C365.232 470.229 355.448 478.024 344.948 484.229C338.372 488.1 331.557 491.334 324.531 493.959C320.315 495.55 316.046 496.876 311.724 498.016C301.648 500.694 291.28 502.152 280.913 502.444C279.428 502.497 277.943 502.523 276.458 502.523C257.208 502.497 237.984 498.493 220.298 490.804C218.946 490.194 217.594 489.584 216.268 488.948C213.563 487.675 210.938 486.323 208.34 484.865C207.041 484.149 205.741 483.407 204.469 482.638C201.923 481.1 199.431 479.483 196.991 477.759C195.745 476.937 194.552 476.036 193.332 475.161C186.146 469.778 179.464 463.627 173.445 456.733C172.438 455.593 171.457 454.453 170.502 453.26C168.566 450.874 166.71 448.408 164.934 445.889C164.032 444.616 163.157 443.317 162.309 442.018C159.763 438.067 157.43 433.984 155.256 429.662C154.566 428.231 153.85 426.799 153.187 425.341C151.172 420.913 149.369 416.326 147.805 411.58C146.346 407.072 145.1 402.512 144.146 397.898C142.846 391.8 141.971 385.622 141.547 379.418C141.335 376.316 141.229 373.214 141.229 370.112C141.229 368.574 141.255 367.009 141.335 365.472C141.494 360.832 141.892 356.218 142.502 351.631C143.987 340.919 146.718 330.393 150.668 320.318C151.225 318.886 151.809 317.454 152.419 316.023C155.494 308.943 159.153 302.076 163.422 295.58C165.119 292.982 166.896 290.41 168.805 287.944C170.688 285.452 172.65 283.012 174.745 280.679C176.786 278.319 178.961 276.013 181.214 273.812C185.51 269.57 190.071 265.619 194.896 262.013C203.302 255.703 212.45 250.373 222.075 246.184C223.454 245.601 224.859 245.018 226.238 244.461C229.048 243.374 231.886 242.34 234.749 241.438C237.613 240.537 240.53 239.715 243.446 239.025C248.166 237.859 251.958 236.931 255.007 236.745C256.227 236.692 257.314 236.745 258.268 236.931C259.25 237.116 260.125 237.461 260.867 237.991C263.121 239.582 264.42 242.79 264.977 248.491C265.056 249.419 265.136 250.453 265.189 251.54C265.507 258.142 263.651 265.301 261.265 271.426C264.261 268.933 267.469 266.759 270.81 264.903C281.947 258.646 294.648 255.65 307.428 254.642C311.273 254.351 315.118 254.218 318.936 254.245C328.8 254.351 341.395 259.415 350.834 258.275C351.471 258.195 352.107 258.116 352.69 258.01C352.902 257.983 353.115 257.93 353.327 257.903C353.671 257.824 354.043 257.771 354.361 257.691C354.626 257.612 354.865 257.532 355.13 257.479C355.368 257.4 355.66 257.347 355.899 257.294C356.164 257.188 356.402 257.108 356.668 257.029C356.853 256.949 357.092 256.896 357.251 256.79C357.543 256.71 357.781 256.604 358.02 256.498C358.179 256.419 358.365 256.366 358.524 256.286C358.762 256.154 359.001 256.021 359.213 255.888C359.346 255.809 359.478 255.756 359.611 255.703C359.85 255.544 360.062 255.411 360.274 255.252C360.38 255.173 360.512 255.093 360.618 255.04C360.831 254.881 361.016 254.722 361.202 254.563C361.308 254.483 361.414 254.404 361.493 254.351C361.706 254.138 361.865 253.953 362.05 253.794C362.13 253.714 362.209 253.635 362.289 253.555C362.448 253.37 362.634 253.157 362.793 252.972C362.846 252.892 362.899 252.813 362.978 252.76C363.137 252.521 363.297 252.309 363.429 252.097C363.482 252.017 363.535 251.964 363.562 251.885C363.721 251.646 363.88 251.408 364.012 251.142C364.039 251.089 364.065 251.036 364.119 250.983C364.251 250.718 364.384 250.453 364.49 250.161C364.516 250.108 364.543 250.055 364.569 250.002C364.702 249.711 364.808 249.419 364.94 249.127C364.967 249.074 364.994 249.021 364.994 249.021C365.1 248.677 365.232 248.358 365.338 248.014V247.961C367.353 241.783 367.513 233.563 373.532 224.893C380.054 215.507 389.6 207.924 390.873 198.008C391.43 193.792 390.475 189.179 387.161 183.823C375.732 165.263 339.194 162.85 320.288 154.949C308.25 149.885 296.477 144.105 285.208 137.476C284.572 137.105 283.936 136.76 283.299 136.362C281.947 135.567 280.621 134.719 279.295 133.897C278.288 133.26 277.28 132.65 276.273 132.014C275.954 131.802 275.663 131.59 275.345 131.378C273.329 130.079 271.341 128.753 269.379 127.401C267.125 125.836 264.871 124.219 262.67 122.549C236.791 103.167 211.893 76.8381 202.109 45.3923C201.26 53.5321 200.438 61.3273 200.279 68.6982C200.253 69.9179 200.253 71.1376 200.279 72.3572C200.385 83.228 202.215 93.2239 207.863 102.318C193.73 101.974 180.79 96.7503 169.097 88.5574C163.9 84.9514 158.915 80.7357 154.222 76.1222C151.862 73.789 149.581 71.3762 147.354 68.8839C128.395 47.6195 113.971 20.3894 104.399 0C104.293 2.75748 104.24 5.54147 104.24 8.35197C104.187 22.378 105.353 36.7752 107.899 50.9868C108.403 53.7973 108.96 56.6608 109.569 59.4713C115.138 84.8189 125.32 109.132 141.123 128.885C108.668 147.048 80.5084 171.653 58.0762 200.58C52.9057 207.288 48.0269 214.182 43.4927 221.314C40.4434 226.033 37.5532 230.886 34.8221 235.791C32.0645 240.722 29.4925 245.733 27.0266 250.798C24.5871 255.888 22.3068 261.032 20.1856 266.255C18.0378 271.479 16.0756 276.755 14.2726 282.111C12.443 287.467 10.8256 292.876 9.3407 298.338C8.59826 301.069 7.90886 303.826 7.24597 306.557C3.98456 320.371 1.78377 334.45 0.723152 348.741C0.192841 355.9 -0.0458022 363.032 0.00723227 370.112C0.139808 393.736 3.3747 416.83 9.36722 438.942C9.95055 441.17 10.5869 443.37 11.2498 445.571C43.8374 555.181 143.721 639.628 263.625 644.745C268.45 644.958 273.276 645.037 278.049 644.984C287.886 644.878 297.644 644.215 307.243 643.049C311.3 642.545 315.356 641.961 319.413 641.299C322.012 640.848 324.637 640.397 327.209 639.893C329.516 639.416 331.796 638.939 334.05 638.435C338.107 637.534 342.137 636.5 346.114 635.412C353.274 633.477 360.327 631.223 367.274 628.704C373.77 626.345 380.134 623.773 386.392 620.936C389.52 619.53 392.596 618.046 395.672 616.508C400.047 614.334 404.369 612.053 408.638 609.641C411.343 608.129 414.021 606.591 416.672 604.974C421.71 601.898 426.642 598.664 431.468 595.297C434.093 593.467 436.718 591.584 439.29 589.649C446.555 584.214 453.555 578.407 460.29 572.282C463.631 569.207 466.919 566.078 470.128 562.843C482.961 549.957 494.548 535.825 504.704 520.712C507.223 516.921 509.662 513.076 512.022 509.152C514.356 505.228 516.636 501.277 518.784 497.221Z"
                    fill={theme.colors.borderLow}
                  />
                </svg>
              </EmptyRow>
            )}
          </TableContainer>
          {initialLoad && records.length > 0 && (
            <TableActionsWrapper>
              <TableHeader showborder="false">
                <Box className="footer-actions">
                  <Box className="per-page-wrapper">
                    <Typography className="per-page-text">Rows per page</Typography>
                    <AuditLogSingleSelectField
                      label=""
                      placeholder=""
                      options={defaultLimitOptions.map((n) => n.name)}
                      value={limit}
                      handleChange={handleLimitChange}
                      noborder="true"
                    />
                  </Box>
                  <Box className="page-counter">
                    <Typography>{getPaginationText()}</Typography>
                  </Box>
                  <Box className="actions-wrapper">
                    <Box className="icon-container left" component="button" onClick={handleStart} disabled={page === 1}>
                      <PaginationEndIcon />
                    </Box>
                    <Box className="icon-container left" component="button" onClick={handlePrev} disabled={page === 1}>
                      <PaginationNextIcon />
                    </Box>
                    <Box
                      className="icon-container"
                      component="button"
                      onClick={handleNext}
                      disabled={getNextDisabled()}
                    >
                      <PaginationNextIcon />
                    </Box>
                    <Box className="icon-container" component="button" onClick={handleEnd} disabled={getNextDisabled()}>
                      <PaginationEndIcon />
                    </Box>
                  </Box>
                </Box>
              </TableHeader>
            </TableActionsWrapper>
          )}
        </AuditLogContainer>
      </ContentWrapper>
    </Container>
  )
}

export default AuditLog
